export interface LibraryTileLike {
  title: string;
  targetTypes?: readonly string[];
}

export interface CatalogSummaryLike {
  candidates: Array<{ targetType: string; status: string }>;
  queueItems: Array<{ status: string }>;
  versions: Array<{ targetType: string }>;
  sources?: Array<{
    fragments?: Array<unknown>;
    integrity?: { blocked?: number } | null;
    metadata?: Record<string, unknown>;
  }>;
  loading: boolean;
}

export interface LibraryTileState {
  badge?: { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' | 'info' };
  stats: string[];
  nextStep?: string;
  nextStepTone?: 'danger' | 'info';
  pendingCount?: number;
  failedCount?: number;
  readyCount?: number;
  blockedCount?: number;
  sourceOnlyCount?: number;
  sourceFragmentCount?: number;
  integrityBlockedCount?: number;
}

export function tileState(item: LibraryTileLike, summary: CatalogSummaryLike): LibraryTileState {
  if (summary.loading) return { stats: ['Loading activity'] };

  if (item.title === 'Catalog Review') {
    const blockedReviews = summary.queueItems.filter((queueItem) => queueItem.status === 'blocked').length;
    const failedReviews = summary.queueItems.filter((queueItem) => queueItem.status === 'failed').length;
    const ready = summary.candidates.filter(
      (candidate) => candidate.status === 'ready_for_human' || candidate.status === 'approved',
    ).length;
    const pending = summary.candidates.filter((candidate) => candidate.status.startsWith('pending')).length;
    const stats = [
      `${summary.candidates.length} candidates`,
      `${summary.queueItems.length} reviews`,
      `${summary.versions.length} versions`,
    ];
    const sourceStats = sourceAuditStats(summary.sources ?? []);
    if (sourceStats.sourceOnly > 0) stats.push(`${sourceStats.sourceOnly} source-only`);
    if (sourceStats.fragments > 0) stats.push(`${sourceStats.fragments} fragments`);
    if (blockedReviews > 0) {
      return {
        badge: { label: `${blockedReviews} blocked`, variant: 'danger' },
        stats,
        nextStep: 'Resolve review blocker',
        nextStepTone: 'danger',
        blockedCount: blockedReviews,
        sourceOnlyCount: sourceStats.sourceOnly,
        sourceFragmentCount: sourceStats.fragments,
        integrityBlockedCount: sourceStats.integrityBlocked,
      };
    }
    if (failedReviews > 0) {
      return {
        badge: { label: `${failedReviews} failed`, variant: 'danger' },
        stats,
        nextStep: 'Retry failed reviews',
        nextStepTone: 'danger',
        failedCount: failedReviews,
        sourceOnlyCount: sourceStats.sourceOnly,
        sourceFragmentCount: sourceStats.fragments,
        integrityBlockedCount: sourceStats.integrityBlocked,
      };
    }
    if (ready > 0) {
      return {
        badge: { label: `${ready} ready`, variant: 'success' },
        stats,
        nextStep: 'Promote or reject candidates',
        nextStepTone: 'info',
        readyCount: ready,
        sourceOnlyCount: sourceStats.sourceOnly,
        sourceFragmentCount: sourceStats.fragments,
        integrityBlockedCount: sourceStats.integrityBlocked,
      };
    }
    if (pending > 0) {
      return {
        badge: { label: `${pending} pending`, variant: 'warning' },
        stats,
        nextStep: 'Waiting on review daemons',
        nextStepTone: 'info',
        pendingCount: pending,
        sourceOnlyCount: sourceStats.sourceOnly,
        sourceFragmentCount: sourceStats.fragments,
        integrityBlockedCount: sourceStats.integrityBlocked,
      };
    }
    return {
      stats,
      nextStep: 'No review work pending',
      nextStepTone: 'info',
      sourceOnlyCount: sourceStats.sourceOnly,
      sourceFragmentCount: sourceStats.fragments,
      integrityBlockedCount: sourceStats.integrityBlocked,
    };
  }

  const targetTypes = item.targetTypes ?? [];
  const candidates = summary.candidates.filter((candidate) => targetTypes.includes(candidate.targetType));
  const versions = summary.versions.filter((version) => targetTypes.includes(version.targetType));
  const failed = candidates.filter((candidate) => candidate.status.endsWith('_failed')).length;
  const ready = candidates.filter(
    (candidate) => candidate.status === 'ready_for_human' || candidate.status === 'approved',
  ).length;
  const pending = candidates.filter((candidate) => candidate.status.startsWith('pending')).length;
  const stats = [`${candidates.length} discoveries`, `${versions.length} promoted`];

  if (failed > 0)
    return {
      badge: { label: `${failed} failed`, variant: 'danger' },
      stats,
      nextStep: 'Review failed checks',
      nextStepTone: 'danger',
      failedCount: failed,
    };
  if (ready > 0)
    return {
      badge: { label: `${ready} ready`, variant: 'success' },
      stats,
      nextStep: 'Promotion available',
      nextStepTone: 'info',
      readyCount: ready,
    };
  if (pending > 0)
    return {
      badge: { label: `${pending} pending`, variant: 'warning' },
      stats,
      nextStep: 'Security and quality checks pending',
      nextStepTone: 'info',
      pendingCount: pending,
    };
  return { stats, nextStep: 'No pending discoveries', nextStepTone: 'info' };
}

export function reviewActionLabel(state: LibraryTileState): string | null {
  if ((state.blockedCount ?? 0) > 0) return `Resolve ${state.blockedCount} blocked`;
  if ((state.failedCount ?? 0) > 0) return `Review ${state.failedCount} failed`;
  if ((state.readyCount ?? 0) > 0) return `Review ${state.readyCount} ready`;
  if ((state.pendingCount ?? 0) > 0) return `Review ${state.pendingCount} pending`;
  return null;
}

export function reviewDestination(item: LibraryTileLike, state?: LibraryTileState): string | null {
  if (item.title === 'Catalog Review') return '/agents/libraries/catalog-review?tab=queue';
  const targetTypes = item.targetTypes?.join(',') ?? '';
  if (!targetTypes) return null;
  const params = new URLSearchParams({ tab: 'candidates', targetTypes });
  if ((state?.failedCount ?? 0) > 0) params.set('statuses', 'security_failed,quality_failed');
  else if ((state?.readyCount ?? 0) > 0) params.set('statuses', 'ready_for_human,approved');
  else if ((state?.pendingCount ?? 0) > 0) params.set('statuses', 'pending_security,pending_quality');
  return `/agents/libraries/catalog-review?${params.toString()}`;
}

export function sourceAuditStats(sources: NonNullable<CatalogSummaryLike['sources']>) {
  let sourceOnly = 0;
  let fragments = 0;
  let integrityBlocked = 0;
  for (const source of sources) {
    const sourceFragments = source.fragments ?? [];
    fragments += sourceFragments.length;
    if (source.metadata?.sourceOnly === true) sourceOnly += 1;
    if ((source.integrity?.blocked ?? 0) > 0) integrityBlocked += 1;
    if (
      source.metadata?.sourceOnly !== true &&
      sourceFragments.some((fragment) => {
        const record = fragment && typeof fragment === 'object' ? (fragment as Record<string, unknown>) : {};
        return record.classification === 'source_only';
      })
    ) {
      sourceOnly += 1;
    }
  }
  return { sourceOnly, fragments, integrityBlocked };
}
